<template>
    <v-container fluid>
        <v-layout v-if="scenario">
            <v-btn class="ml-0 my-0" @click="backToScenario">{{$t('back')}}</v-btn>
            <div class="display-1">
                Audit -
                <span v-if="scenario" @click="backToScenario" class="name">{{ scenario.name}}</span>
            </div>
        </v-layout>
        <v-layout align-center>
            <v-spacer></v-spacer>
            <span class="mr-3">
                <v-select
                    v-if="module === 'node' || module === 'nlp'"
                    :label="$t('action') | capitalize"
                    :items="methodList"
                    v-model="method"
                    clearable
                ></v-select>
            </span>
            <span class="mr-3">
                <v-autocomplete
                    v-if="module === 'node'"
                    :label="$t('node') | capitalize"
                    :items="nodeList"
                    item-text="name"
                    item-value="id"
                    v-model="nodeId"
                    clearable
                ></v-autocomplete>
            </span>
            <span class="mr-3">
                <v-select
                    v-if="module === 'nlp'"
                    :label="$t('NLP') | capitalize"
                    :items="nlpModuleList"
                    v-model="nlpModule"
                    clearable
                ></v-select>
            </span>
            <span class="mr-3">
                <v-select
                    :label="$t('module') | capitalize"
                    :items="moduleList"
                    v-model="module"
                    clearable
                ></v-select>
            </span>
            <span class="mr-3">
                <v-text-field
                    :label="$t('user') | capitalize"
                    v-model="userEmail"
                    clearable
                ></v-text-field>
            </span>
            <TimeRangePicker ref="TimeRangePicker" v-model="timeRange" button-class-name="ml-0"></TimeRangePicker>
            <v-btn @click="refresh" class="mr-0">
                <v-icon>mdi-filter</v-icon> /
                <v-icon>mdi-refresh</v-icon>
            </v-btn>
        </v-layout>
        <SimpleDataTable
            :headers="headers"
            :items="items"
            :pagination.sync="pagination"
        >
            <template v-slot:action="props">
                <v-btn @click="openDataViewer(props.item)">Data</v-btn>
            </template>
        </SimpleDataTable>
        <JSONViewer ref="JSONViewer"></JSONViewer>
    </v-container>
</template>

<script>
import ScenarioService from "@/services/scenario.service";
import SimpleDataTable from "@/modules/fptai/components/SimpleDataTable";
import TimeRangePicker from "@/modules/fptai/components/TimeRangePicker";
import JSONViewer from "@/components/JSONViewer";

export default {
    name: "ScenarioAudit",
    components: { JSONViewer, TimeRangePicker, SimpleDataTable },
    data () {
        return {
            scenario: null,
            pagination: {
                page: 1,
                rowsPerPage: 10,
                totalItems: 0,
                ordering: '-@timestamp'
            },
            items: [],
            timeRange: 'thisYear',
            userEmail: '',
            method: '',
            methodList: [
                {
                    text: 'POST',
                    value: 'POST'
                }, {
                    text: 'PATCH',
                    value: 'PATCH'
                }, {
                    text: 'DELETE',
                    value: 'DELETE'
                }
            ],
            module: '',
            moduleList: [
                {
                    text: 'node',
                    value: 'node'
                }, {
                    text: 'setting',
                    value: 'setting'
                }, {
                    text: 'nlp',
                    value: 'nlp'
                }, {
                    text: 'variable',
                    value: 'variable'
                }
            ],
            nlpModule: '',
            nlpModuleList: [
                {
                    text: 'sample',
                    value: 'sample'
                }, {
                    text: 'intent',
                    value: 'intent'
                }, {
                    text: 'entity',
                    value: 'entity'
                }, {
                    text: 'keyword',
                    value: 'keyword'
                }, {
                    text: 'dictionary',
                    value: 'dictionary'
                }
            ],
            nodeList: [],
            nodeId: ''
        }
    },
    computed: {
        headers () {
            return this.$appService.correctHeaders([
                {
                    text: this.$t('time'),
                    value: '@timestamp'
                }, {
                    text: this.$t('user'),
                    value: 'user.email'
                }, {
                    text: this.$t('method'),
                    value: 'request.method'
                }, {
                    text: this.$t('path'),
                    value: 'request.path'
                }, {
                    text: this.$t('requestData'),
                    value: 'action',
                    type: 'custom',
                    id: 'action'
                }
            ], true)
        }
    },
    mounted () {
        let stateScenario = this.$store.state.scenario.scenario
        let id = this.$route.params.scenarioId
        if (stateScenario && stateScenario.id === id) {
            this.scenario = stateScenario
        } else {
            ScenarioService.read(id).then(
                result => this.scenario = result.data,
                this.$appService.handleRequestError(' can not load scenario')
            )
        }
        this.refresh()
        let treeData = this.$store.state.scenario.treeData
        let nodeList = treeData ? treeData.nodeList : null
        if (!nodeList || nodeList.length === 0) {
            ScenarioService.NodeService.search(this.$route.params.scenarioId, { page: 1, page_size: 100 }).then(
                result => {
                    this.nodeList = result.data.data
                }
            )
        } else {
            this.nodeList = nodeList
        }
    },
    methods: {
        backToScenario () {
            this.$router.push(`/scenarios/${this.scenario.id}`)
        },
        buildRegex () {
            const scenarioId = this.$route.params.scenarioId
            switch (this.module) {
                case 'node':
                    if (this.nodeId) {
                        return '/api/scenarios/' + scenarioId + '/nodes/' + this.nodeId + '/*'
                    }
                    return '/api/scenarios/' + scenarioId + '/nodes/*'
                case 'nlp':
                    if (this.nlpModule) {
                        return '/api/scenarios/' + scenarioId + '/nlp/' + this.nlpModule + '/*'
                    }
                    return '/api/scenarios/' + scenarioId + '/nlp/*'
                case 'setting':
                    return '/api/scenarios/' + scenarioId + '/'
                case 'variable':
                    return '/api/scenarios/' + scenarioId + '/variables/*'
            }
            return '/api/scenarios/' + scenarioId + '/*'
        },
        refresh () {
            let request_method = this.module === 'node' || this.module === 'nlp' ? this.method || '*' : '*'
            ScenarioService.audit(
                this.$route.params.scenarioId,
                {
                    ...this.$refs.TimeRangePicker.getTimestamp(),
                    ...this.pagination,
                    regex: this.buildRegex(),
                    request_method: request_method,
                    user_email: this.userEmail ? `*${this.userEmail}*` : '*'
                }
            ).then(
                result => {
                    this.pagination.totalItems = result.data.count
                    this.items = result.data.data.map(item => ({
                        '@timestamp': this.$filters.formatDateTime(item['@timestamp']),
                        'user.email': item.user.email,
                        'request.method': item.request.method,
                        'request.path': item.request.path,
                        'request.data': item.request.data,
                        response_status: item.response.status,
                    }))
                }
            )
        },
        openDataViewer (item) {
            this.$refs.JSONViewer.open(item['request.data'])
        }
    },
    watch: {
        pagination () {
            this.refresh()
        }
    }
}
</script>

<style scoped>
    .name {
        color: blue;
        cursor: pointer;
    }
</style>
